import {BehaviorSubject, map, Observable} from "rxjs";
import {IChatSetting} from "@listat/r2-core";
import { OfficeChatSettingV1, SETTING_MODEL, SETTING_TOKEN_LENGTH } from "./office-chat-setting-v1";
import {IOfficeChatSetting} from "./office-chat-setting-v1.types";
import {IQuickAction, ISkill} from "@core/base/chat-setting-base";
import {OfficeChatRepository} from "@core/office/office-chat-repository/office-chat-repository";
import {ISessionInfo} from "@core/base/session-base.types";
import {IChatRepositorySetting} from "@core/office/office-chat-repository/office-chat-repository.types";
import {OfficeChatSettingBase} from "@core/office/office-chat-setting/office-chat-setting-base";
import { OfficeService } from "@core/office/office-service/office-service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";

export class OfficeChatSettingAdapterV1 extends OfficeChatSettingV1 implements OfficeChatSettingBase {
    private officeService = this.office;
    private sessionService = this.session;
    private quickActions: BehaviorSubject<IQuickAction[]> = new BehaviorSubject<IQuickAction[]>([]);
    private skills: BehaviorSubject<ISkill[]> = new BehaviorSubject<ISkill[]>([]);

    constructor(
        private office: OfficeService,
        private session: OfficeSessionService,
        private chatRepository: OfficeChatRepository,
    ) {
        super()
        this.defineHandlerForSessionChange();
    }

    public $getSetting(): Observable<IChatSetting> {
        const officeSetting = this.$setting;
        return officeSetting.pipe(map(value => {
            return this.transformChatSetting(value);
        }))
    }

    public getSetting(): IChatSetting {
        const setting = super.get();
        return this.transformChatSetting(setting);
    }

    public setSetting(setting: IChatSetting): IChatSetting {
        const chatSetting = this.transformChatSetting(super.get());
        const newSetting = {
            ...chatSetting,
            ...setting,
            options: {
                ...chatSetting.options || {},
                ...setting.options || {}
            }
        }
        
        const officeSetting = this.transformOfficeSetting(newSetting);

        if (!Object.values(SETTING_MODEL).includes(officeSetting.model)) {
            officeSetting.model = SETTING_MODEL.GPT_41;
        }

        this.saveSettingToRepository(newSetting)
        super.set(officeSetting)
        return newSetting;
    }

    public getSettingOriginal<T>(): T {
        return super.get() as T;
    }

    public getQuickActionsDefault(): IQuickAction[] {
        const officeHost = this.officeService.getHost();
        return officeHost.getQuickActions();
    }

    public $getQuickActions(): Observable<IQuickAction[]> {
        return this.quickActions.asObservable()
    }

    public setQuickActions(newActions: IQuickAction[]): IQuickAction[] {
        this.quickActions.next(newActions)
        const chatSetting = this.transformChatSetting(super.get());
        this.saveSettingToRepository(chatSetting)
        return this.quickActions.getValue();
    }

    public $getSkills(): Observable<ISkill[]> {
        return this.skills.asObservable()
    }

    public setSkills(newSkills: ISkill[]): ISkill[] {
        this.skills.next(newSkills)
        const chatSetting = this.transformChatSetting(super.get());
        this.saveSettingToRepository(chatSetting)
        return this.skills.getValue();
    }

    public onDestroy(): void {}

    private initQuickActionsDefault(): void {
        const officeHost = this.officeService.getHost();
        this.setQuickActions(officeHost.getQuickActions());
    }

    private transformChatSetting(officeSetting: IOfficeChatSetting): IChatSetting {
        return {
            systemMessage: officeSetting.role_information,
            options: {
                maxGptTokensForEachCall: officeSetting.max_tokens,
                model: officeSetting.model,
            }
        }
    }

    private transformOfficeSetting(chatSetting: IChatSetting): IOfficeChatSetting {
        const originalSetting = super.get();
        const setting = {
            ...originalSetting,
        }
        if (chatSetting.systemMessage) {
            setting.role_information = chatSetting.systemMessage;
        }

        if (chatSetting.options && chatSetting.options.maxGptTokensForEachCall) {
            setting.max_tokens = chatSetting.options.maxGptTokensForEachCall;
        }

        if (chatSetting.options && chatSetting.options.model) {
            setting.model = chatSetting.options.model;
        }

        return setting;
    }

    private defineHandlerForSessionChange(): void {
        this.sessionService.sessionInfo$.subscribe(
            this.handlerForSessionChange.bind(this)
        )
    }

    private async handlerForSessionChange(info: ISessionInfo | null): Promise<void> {
        if (info && info.user_id) {
            this.chatRepository.defineSettingKey(`${info.user_id}-${Office.context.host}`)
            const setting = await this.chatRepository.readSetting()
            if (setting) {
                this.quickActions.next(setting.quickActions)
                this.skills.next(setting.skills ?? [])
                const newSetting = this.getSettingFromRepositorySetting(setting);
                this.setSetting(newSetting);
                return
            }
            this.initQuickActionsDefault()
        }
    }

    private saveSettingToRepository(setting: IChatSetting): void {
        const repositorySetting = {
            ...setting,
            quickActions: this.quickActions.getValue(),
            skills: this.skills.getValue(),
        }
        this.chatRepository.writeSetting(repositorySetting)
    }

    private getSettingFromRepositorySetting(repoSetting: IChatRepositorySetting): IChatSetting {
        const isHasModel = repoSetting?.options?.model;
        const isHasMaxTokens = repoSetting?.options?.maxGptTokensForEachCall;

        if (repoSetting?.options && isHasModel && isHasMaxTokens) {
            const isModelGPT4 = repoSetting.options?.model === SETTING_MODEL.GPT_41;
            const isMaxTokensMax = repoSetting.options?.maxGptTokensForEachCall === SETTING_TOKEN_LENGTH.huge;

            if (isModelGPT4 && isMaxTokensMax) {
                repoSetting.options.maxGptTokensForEachCall = SETTING_TOKEN_LENGTH.long;
            }
        }

        return {
            systemMessage: repoSetting.systemMessage,
            options: repoSetting.options
        }
    }
}
